/**
 * ExpauseSheetContainer Component
 *
 * Renders all sheets in the stack with:
 * - Z-index management for stacking
 * - ESC key handling to close topmost sheet
 * - Auto display mode selection (dialog vs bottom sheet)
 * - Pass-through of close permissions
 */

import React, { useEffect, useRef } from 'react';
import { useIsLandscape } from '@/presentation/responsiveDesign/responsiveDesign';
import { SHEET_CONSTANTS } from '@/presentation/constants/sheetConstants';
import { BottomSheetView } from './BottomSheetView';
import { DialogView } from './DialogView';
import type { SheetDisplayMode } from '../config/ExpauseSheet.types';

// Import context hook from main file (will be defined there)
import { useExpauseSheetContext } from '../sheetImpl/ExpauseSheet';

/**
 * Container component that renders all sheets in the stack
 */
export const ExpauseSheetContainer: React.FC = () => {
  const { sheets, hideSheet } = useExpauseSheetContext();
  const isLandscape = useIsLandscape();
  const hideSheetRef = useRef(hideSheet);

  // Keep ref in sync with hideSheet function
  useEffect(() => {
    hideSheetRef.current = hideSheet;
  }, [hideSheet]);

  // Handle ESC key to close topmost sheet
  useEffect(() => {
    if (sheets.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const topSheet = sheets[sheets.length - 1];
        const canClose = topSheet.closeOnClickOutside !== false;

        if (canClose) {
          e.preventDefault();
          console.log('⌨️ [ExpauseSheet] ESC key pressed, closing topmost sheet');
          hideSheetRef.current();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sheets]);

  if (sheets.length === 0) {
    return null;
  }

  // Render all sheets in the stack
  return (
    <>
      {sheets.map((sheet, index) => {
        const isTopSheet = index === sheets.length - 1;
        const zIndex = SHEET_CONSTANTS.baseZIndex + index * SHEET_CONSTANTS.zIndexIncrement;

        // Determine display mode for this sheet
        let displayMode: SheetDisplayMode = 'auto';
        if (sheet.forceBottomSheet) {
          displayMode = 'bottomSheet';
        } else if (sheet.forceDialog) {
          displayMode = 'dialog';
        } else {
          // Auto mode: use orientation to decide
          displayMode = isLandscape ? 'dialog' : 'bottomSheet';
        }

        // Only top sheet can be closed via backdrop/drag
        const canClose = isTopSheet && sheet.closeOnClickOutside !== false;

        // Render based on display mode
        if (displayMode === 'bottomSheet') {
          return (
            <BottomSheetView
              key={sheet.id}
              state={sheet}
              onClose={hideSheet}
              zIndex={zIndex}
              canClose={canClose}
            />
          );
        }

        return (
          <DialogView
            key={sheet.id}
            state={sheet}
            onClose={hideSheet}
            zIndex={zIndex}
            canClose={canClose}
          />
        );

      })}
    </>
  );
};
